import { Link, useRouterState } from "@tanstack/react-router";
import {
  Calendar,
  CheckSquare,
  ClipboardList,
  Compass,
  FileText,
  HeartPulse,
  Home,
  LayoutDashboard,
  Lightbulb,
  Mail,
  Notebook,
  PiggyBank,
  Receipt,
  ReceiptText,
  Rocket,
  Settings,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useMe } from "@/hooks/useMe";
import { useTaxProjection } from "@/hooks/useTax";

type BadgeTone = "primary" | "warning" | "success";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  badge?: string;
  badgeTone?: BadgeTone;
  ownerOnly?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
  ownerOnly?: boolean;
}

const allSections: NavSection[] = [
  {
    label: "Get started",
    items: [
      { to: "/setup", label: "Setup Center", icon: Rocket, badge: "Start" },
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
    ],
  },
  {
    label: "Tax & wealth",
    ownerOnly: true,
    items: [
      { to: "/strategy", label: "Strategy Hub", icon: Compass, ownerOnly: true },
      { to: "/strategy/tax-plays", label: "Tax Plays", icon: Lightbulb, badge: "New", ownerOnly: true },
      { to: "/strategy/entity", label: "Entity Transition", icon: Sparkles, ownerOnly: true },
      { to: "/strategy/cash", label: "Cash Allocation", icon: PiggyBank, ownerOnly: true },
      { to: "/strategy/retirement", label: "Retirement", icon: HeartPulse, ownerOnly: true },
      { to: "/strategy/hire-family", label: "Hire Family", icon: UserPlus, ownerOnly: true },
      { to: "/taxes", label: "Taxes & Estimates", icon: Receipt, ownerOnly: true },
      { to: "/tax-intake", label: "Tax Intake", icon: ClipboardList, ownerOnly: true },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/money", label: "Money", icon: Wallet },
      { to: "/invoices", label: "Invoices", icon: ReceiptText },
      { to: "/email", label: "Email", icon: Mail },
      { to: "/personal", label: "Personal Hub", icon: Home, badge: "New", ownerOnly: true },
      { to: "/compliance", label: "Compliance", icon: ShieldCheck },
      { to: "/calendar", label: "Calendar", icon: Calendar },
      { to: "/documents", label: "Documents", icon: FileText },
      { to: "/people", label: "People", icon: Users },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/tasks", label: "Tasks", icon: CheckSquare },
      { to: "/notes", label: "Notes", icon: Notebook },
    ],
  },
  {
    label: "Admin",
    ownerOnly: true,
    items: [
      { to: "/settings", label: "Settings & Team", icon: Settings, ownerOnly: true },
    ],
  },
];

const badgeToneClass: Record<BadgeTone, string> = {
  primary: "bg-primary/15 text-primary",
  warning: "bg-amber-500/20 text-amber-300",
  success: "bg-emerald-500/20 text-emerald-300",
};

function CalderaMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 28 28" className={cn("shrink-0", className)} aria-hidden>
      <rect width="28" height="28" rx="6" fill="#d97706" fillOpacity="0.12" />
      <path
        d="M5 19 L12 10.5 L14 12.5 L16 10.5 L23 19"
        fill="none"
        stroke="#737373"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M14 17 Q12.5 13 14 8.5 Q15.5 13 14 17Z" fill="#d97706" />
      <circle cx="14" cy="8" r="1" fill="#fbbf24" />
    </svg>
  );
}

export function Sidebar() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useMe();
  const isOwner = me?.isOwner ?? true;

  const projection = useTaxProjection();
  const intakeBadge = (() => {
    if (!isOwner || projection.isLoading || !projection.data) return undefined;
    return projection.data.projection.hasBlockingGaps
      ? { label: "Action needed", tone: "warning" as BadgeTone }
      : { label: "Done", tone: "success" as BadgeTone };
  })();

  const sections: NavSection[] = allSections
    .filter((s) => !s.ownerOnly || isOwner)
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => !item.ownerOnly || isOwner)
        .map((item) =>
          item.to === "/tax-intake" && intakeBadge
            ? { ...item, badge: intakeBadge.label, badgeTone: intakeBadge.tone }
            : item,
        ),
    }));

  return (
    <aside className="hidden w-56 shrink-0 bg-sidebar text-zinc-400 lg:flex lg:flex-col">
      <div className="flex h-12 items-center gap-2.5 border-b border-white/6 px-4">
        <CalderaMark className="h-6 w-6" />
        <span className="text-sm font-semibold text-zinc-100">Caldera</span>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        {sections.map((section) => (
          <div key={section.label} className="mb-3">
            <div className="mb-0.5 px-2 pt-2 text-2xs font-medium uppercase tracking-wider text-zinc-500">
              {section.label}
            </div>
            <ul>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active =
                  item.to === "/"
                    ? path === "/"
                    : path === item.to || path.startsWith(item.to + "/");
                const tone = item.badgeTone ?? "primary";
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={cn(
                        "group flex items-center gap-2 rounded-md px-2 py-[5px] text-[13px] transition-colors",
                        active
                          ? "bg-white/8 font-medium text-zinc-100"
                          : "text-zinc-400 hover:bg-white/5 hover:text-zinc-200",
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-4 w-4 shrink-0",
                          active ? "text-primary" : "text-zinc-500 group-hover:text-zinc-300",
                        )}
                        strokeWidth={active ? 2 : 1.5}
                        aria-hidden
                      />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge && (
                        <span
                          className={cn(
                            "rounded px-1.5 py-px text-2xs font-medium",
                            badgeToneClass[tone],
                          )}
                        >
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/6 px-4 py-3">
        <p className="text-2xs text-zinc-500">
          {isOwner ? "S-Corp tax management" : "Confidential"}
        </p>
      </div>
    </aside>
  );
}
